import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection, importProvidersFrom } from '@angular/core';
import {provideRouter, withInMemoryScrolling} from '@angular/router';

import { routes } from './app.routes';
import { de_DE, provideNzI18n } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import ar from '@angular/common/locales/ar';
import uk from '@angular/common/locales/uk';
import tr from '@angular/common/locales/tr';
import ru from '@angular/common/locales/ru';
import pl from '@angular/common/locales/pl';
import fr from '@angular/common/locales/fr';
import { FormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {HttpClient, provideHttpClient, withInterceptors} from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { NzIconModule, provideNzIcons } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import {
  AlertOutline,
  BankOutline,
  BugOutline,
  BulbOutline,
  CloudOutline,
  ContactsOutline,
  DeleteOutline,
  DownloadOutline,
  EditOutline,
  EnvironmentOutline,
  ExperimentOutline,
  EyeInvisibleOutline,
  EyeOutline,
  FileExcelOutline,
  FilePdfOutline,
  FireOutline,
  FolderOpenOutline,
  GatewayOutline,
  GlobalOutline,
  HomeOutline,
  InboxOutline,
  MailOutline,
  MedicineBoxOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  MoonOutline,
  NumberOutline,
  PhoneOutline,
  PlusOutline,
  QrcodeOutline,
  RocketOutline,
  SafetyOutline,
  SaveOutline,
  SettingOutline,
  ThunderboltOutline,
  ToolOutline,
  UpCircleOutline,
  UserOutline,
  VideoCameraOutline,
  WarningFill,
  DashboardOutline,
  BookOutline,
  LikeOutline,
  DislikeOutline,
  MessageOutline,
  FileSearchOutline,
  SearchOutline,
  NotificationOutline,
  LinkOutline,
  SafetyCertificateOutline,
  ExportOutline,
  KeyOutline,
  BuildOutline,
  MobileOutline,
  InsuranceOutline,
  SyncOutline,
  ContainerOutline,
  DeploymentUnitOutline,
  FileTextOutline,
  AppstoreOutline,
  MenuOutline,
  LockOutline,
  WifiOutline,
  LoginOutline,
  LogoutOutline,
  SolutionOutline,
  StarOutline,
  RobotOutline,
  ReloadOutline,
  DatabaseOutline,
  BellOutline,
  TeamOutline,
  ArrowLeftOutline,
  WarningOutline,
  StopOutline,
  CommentOutline,
  FilterOutline,
  InfoOutline,
  FileProtectOutline,
  PoweroffOutline,
  PlayCircleOutline,
  CarOutline,
  IdcardOutline,
  HistoryOutline,
  CloseCircleOutline,
  ApiOutline,
  CustomerServiceOutline,
  DragOutline,
  PushpinOutline,
  UnlockOutline,
  ShopOutline,
  CameraOutline,
  SecurityScanOutline,
  CompassOutline,
  PlusCircleOutline,
  FileImageOutline,
  ApartmentOutline,
  PictureOutline,
  MinusOutline,
  BorderOutline,
  CaretDownOutline,
  UndoOutline,
  RedoOutline,
  BgColorsOutline,
  OrderedListOutline,
  CheckCircleTwoTone,
  ClearOutline,
  FormOutline,
  ArrowRightOutline,
  HeartOutline,
  ShoppingOutline,
  CheckSquareOutline,
  ShoppingCartOutline,
  EuroOutline,
  MinusCircleOutline,
  ArrowDownOutline,
  ArrowUpOutline,
  ToolTwoTone,
  EnvironmentTwoTone,
  UnorderedListOutline,
  DollarOutline,
  CreditCardOutline,
  BankTwoTone
} from '@ant-design/icons-angular/icons';
import {authInterceptor} from './interceptors/auth-interceptor.service';
import {MarkdownModule} from 'ngx-markdown';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { CompanyService } from './services/company.service';
import { CompanyThemeService } from './services/company-theme.service';

// Factory function to create the TranslateHttpLoader
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Register German locale
registerLocaleData(de);
registerLocaleData(en);
registerLocaleData(uk);
registerLocaleData(tr);
registerLocaleData(ar);
registerLocaleData(ru);
registerLocaleData(pl);
registerLocaleData(fr);
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled' })),
    provideNzI18n(de_DE),
    provideAnimationsAsync(),
    { provide: NZ_CONFIG, useValue: { message: { nzDuration: 5000 } } },
    provideHttpClient(withInterceptors([authInterceptor])),
    // Import necessary Angular and third-party modules
    importProvidersFrom(
      BrowserModule, // Includes CommonModule, so no need to import separately
      BrowserAnimationsModule,
      FormsModule,
      NzIconModule,
      NzMenuModule,
      MarkdownModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    // Register the icons for ng-zorro-antd
    provideNzIcons([
      HomeOutline,
      SettingOutline,
      UserOutline,
      BankOutline,
      MenuFoldOutline,
      MenuUnfoldOutline,
      BulbOutline,
      PlusOutline,
      MailOutline,
      PhoneOutline,
      AlertOutline,
      EnvironmentOutline,
      GlobalOutline,
      ToolOutline,
      QrcodeOutline,
      SaveOutline,
      EditOutline,
      FileExcelOutline,
      DownloadOutline,
      ContactsOutline,
      BulbOutline,
      MoonOutline,
      FireOutline,
      ExperimentOutline,
      RocketOutline,
      UpCircleOutline,
      MedicineBoxOutline,
      BugOutline,
      EyeOutline,
      EyeInvisibleOutline,
      GatewayOutline,
      VideoCameraOutline,
      NumberOutline,
      DeleteOutline,
      AlertOutline,
      ThunderboltOutline,
      SafetyOutline,
      ExperimentOutline,
      CloudOutline,
      WarningFill,
      FolderOpenOutline,
      InboxOutline,
      FilePdfOutline,
      DashboardOutline,
      BookOutline,
      LikeOutline,
      DislikeOutline,
      MessageOutline,
      FileSearchOutline,
      SearchOutline,
      NotificationOutline,
      LinkOutline,
      SafetyCertificateOutline,
      ExportOutline,
      KeyOutline,
      BuildOutline,
      MobileOutline,
      InsuranceOutline,
      SyncOutline,
      ContainerOutline,
      DeploymentUnitOutline,
      FileTextOutline,
      AppstoreOutline,
      MenuOutline,
      LockOutline,
      WifiOutline,
      LoginOutline,
      LogoutOutline,
      SolutionOutline,
      StarOutline,
      RobotOutline,
      ReloadOutline,
      DatabaseOutline,
      BellOutline,
      TeamOutline,
      ArrowLeftOutline,
      WarningOutline,
      StopOutline,
      CommentOutline,
      FilterOutline,
      InfoOutline,
      FileProtectOutline,
      PoweroffOutline,
      CarOutline,
      IdcardOutline,
      HistoryOutline,
      CloseCircleOutline,
      PlayCircleOutline,
      ApiOutline,
      CustomerServiceOutline,
      DragOutline,
      PushpinOutline,
      UnlockOutline,
      ShopOutline,
      CameraOutline,
      SecurityScanOutline,
      CompassOutline,
      PlusCircleOutline,
      FileImageOutline,
      ApartmentOutline,
      PictureOutline,
      MinusOutline,
      BorderOutline,
      CaretDownOutline,
      UndoOutline,
      RedoOutline,
      BgColorsOutline,
      OrderedListOutline,
      CheckCircleTwoTone,
      ClearOutline,
      FormOutline,
      ArrowRightOutline,
      HeartOutline,
      ShoppingOutline,
      CheckSquareOutline,
      ShoppingCartOutline,
      EuroOutline,
      MinusCircleOutline,
      ArrowDownOutline,
      ArrowUpOutline,
      ToolTwoTone,
      EnvironmentTwoTone,
      BuildOutline,
      UnorderedListOutline,
      DollarOutline,
      CreditCardOutline,
      BankTwoTone
    ]),
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService) => {
        return () => {
          // Get the saved language from localStorage or use default 'en'
          const savedLanguage = localStorage.getItem('selectedLanguage') || 'de';
          translate.setDefaultLang(savedLanguage);
          translate.use(savedLanguage);
        };
      },
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        authService: AuthService,
        companyService: CompanyService,
        companyThemeService: CompanyThemeService
      ) => {
        return () => {
          if (!authService.isCurrentCompanyUser() || !authService.isAuthenticated()) {
            companyThemeService.clearCompanyThemeColor();
            return Promise.resolve();
          }

          return firstValueFrom(
            companyService.getCompanyDetails().pipe(
              map((company) => companyThemeService.applyCompanyThemeColor(company?.themeColor)),
              catchError(() => {
                companyThemeService.applyCompanyThemeColor(null);
                return of(null);
              })
            )
          );
        };
      },
      deps: [AuthService, CompanyService, CompanyThemeService],
      multi: true,
    }
  ]
};
